import {parseISO, subMonths, isAfter} from 'date-fns';
import {Card, Chips, Infobox, OptionType} from 'platform/components';
import {Hide, Show, VStack} from 'platform/foundation';

import {Trans} from 'react-i18next';
import {useDispatch} from 'react-redux';

import {isNil} from 'ramda';

import {checkoutApi, TypeOfSale, useGetVehicleV2Query} from '@dms/api/sales';
import {i18n} from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError, Nullish, TestIdProps, suffixTestId, useBoolean, useToggle} from 'shared/web';

const abroadSaleOptions: OptionType<TypeOfSale>[] = [
  {
    label: i18n.t('entity.checkout.labels.abroadSaleEU'),
    value: 'inEurope',
  },
  {
    label: i18n.t('entity.checkout.labels.abroadSaleOutsideEU'),
    value: 'outOfEurope',
  },
];

interface CheckoutOrderBillingAbroadProps extends TestIdProps {
  readonly?: boolean;
  checkoutId: string;
  orderId: string;
  typeOfSale?: TypeOfSale;
  handleChangeTypeOfSaleVehicle?: (orderId: string, value: TypeOfSale) => Promise<void>;
  vehicleId?: string | Nullish;
}

export const CheckoutOrderBillingAbroad = ({
  readonly,
  orderId,
  typeOfSale,
  handleChangeTypeOfSaleVehicle,
  vehicleId,
  ...rest
}: CheckoutOrderBillingAbroadProps) => {
  const {data: vehicle} = useGetVehicleV2Query(
    {vehicleId: vehicleId ?? ''},
    {skip: isNil(vehicleId)}
  );

  const [abroadSaleOpen, toggleAbroadSaleOpen] = useToggle(typeOfSale !== 'inland');
  const [isLoading, setIsLoading, stopIsLoading] = useBoolean();

  const dispatch = useDispatch();

  const toggleAbroadSale = async () => {
    if (abroadSaleOpen) {
      await onChangeTypeOfSaleVehicle();
    }
    toggleAbroadSaleOpen();
  };

  const onChangeTypeOfSaleVehicle = (value?: string[] | Nullish) => {
    const typeOfSale = (value?.[0] as TypeOfSale) || 'inland';

    setIsLoading();
    handleChangeTypeOfSaleVehicle?.(orderId, typeOfSale)
      .then(() => dispatch(checkoutApi.util.invalidateTags([{type: 'offer', id: orderId}])))
      .catch((error: {response: ApiError}) => handleApiError(error.response))
      .finally(stopIsLoading);
  };

  const inputDate = vehicle?.firstRegistrationOn ? parseISO(vehicle?.firstRegistrationOn) : null;
  const sixMonthsAgo = subMonths(new Date(), 6);

  const isVehicleMileageLow = vehicle?.mileage && vehicle.mileage < 6000;
  const isVehicleNew = inputDate ? isAfter(inputDate, sixMonthsAgo) : false;

  return (
    <Card
      variant="inlineGrey"
      title={i18n.t('entity.checkout.labels.abroadSale')}
      control={{
        type: 'switch',
        onChange: toggleAbroadSale,
        value: abroadSaleOpen,
        isDisabled: readonly,
        'data-testid': suffixTestId('abroadSaleOpen', rest),
      }}
      onExpandButtonClick={readonly ? undefined : toggleAbroadSale}
      isExpanded={abroadSaleOpen}
      data-testid={suffixTestId('abroadSale', rest)}
    >
      <VStack spacing={4}>
        <Chips
          options={abroadSaleOptions}
          value={[typeOfSale as string]}
          isDisabled={readonly ?? isLoading}
          onChange={onChangeTypeOfSaleVehicle}
          data-testid={suffixTestId(`abroadSale`, rest)}
        />
        <Show when={typeOfSale === 'outOfEurope'}>
          <Infobox
            variant="warning"
            data={[
              {value: i18n.t('entity.checkout.infobox.outOfEurope.text.first')},
              {
                list: [
                  {value: i18n.t('entity.checkout.infobox.outOfEurope.list.first')},
                  {value: i18n.t('entity.checkout.infobox.outOfEurope.list.second')},
                  {value: i18n.t('entity.checkout.infobox.outOfEurope.list.third')},
                ],
              },
              {value: i18n.t('entity.checkout.infobox.outOfEurope.text.second')},
            ]}
          />
        </Show>
        <Show when={typeOfSale === 'inEurope'}>
          <Show when={isVehicleMileageLow || isVehicleNew}>
            <Infobox
              variant="warning"
              data={[
                {
                  value: (
                    <Trans
                      i18nKey="entity.checkout.infobox.inEurope.newCar.text.first"
                      components={{bold: <strong />}}
                    />
                  ),
                },
              ]}
            />
          </Show>
          <Hide when={isVehicleMileageLow || isVehicleNew}>
            <Infobox
              variant="warning"
              data={[
                {
                  value: i18n.t('entity.checkout.infobox.inEurope.usedCar.text.first'),
                },
                {
                  list: [
                    {
                      value: (
                        <Trans
                          i18nKey="entity.checkout.infobox.inEurope.usedCar.list.first"
                          components={{bold: <strong />}}
                        />
                      ),
                    },
                    {
                      value: (
                        <Trans
                          i18nKey="entity.checkout.infobox.inEurope.usedCar.list.second"
                          components={{bold: <strong />}}
                        />
                      ),
                    },
                  ],
                },
              ]}
            />
          </Hide>
        </Show>
      </VStack>
    </Card>
  );
};
